// Components
export { AeraButton } from '@aera/ui'
export type { AeraButtonProps, AeraButtonVariant, AeraButtonSize, AeraButtonTextSize, AeraButtonPaddingSize, AeraButtonTextColor, AeraButtonFontWeight } from '@aera/ui'
export { AeraDataTable } from './components/AeraDataTable'
export { WorkerTabSwitcher } from './components/WorkerTabSwitcher'
export { RecordDrawer } from './components/RecordDrawer'
export type { RecordDrawerTab } from './components/RecordDrawer'
export { TransactionHistoryTab } from './components/TransactionHistoryTab'
export {
  MoneyUndoConfirmSheet,
  formatMoveUndoSummaryLine,
} from './components/MoneyUndoConfirmSheet'
export { HistoryActorAvatar } from './components/HistoryActorAvatar'
export { ImageViewer } from './components/ImageViewer'
export { FilterBar } from './components/FilterBar'
export { DataTableSearchField } from './components/DataTableSearchField'
export type { DataTableSearchFieldProps } from './components/DataTableSearchField'
export { DrawerFieldShell, DrawerReadonlyValue } from './components/DrawerFieldShell'
export {
  DataTableSkeleton,
  DataTablePaginationSkeleton,
} from './components/DataTableSkeleton'
export { ThaiDatetimeText } from './components/ThaiDatetimeText'
export { StatusBadge } from './components/StatusBadge'
export { CellRenderer } from './components/CellRenderer'
export { EditableProperty } from './components/EditableProperty'
export { AeraImage, DataTableImage } from './components/DataTableImage'
export type { AeraImageProps, DataTableImageProps } from './components/DataTableImage'

// Built-in schemas
export { BillLinIncomeSchema, BillLinExpenseSchema, BillLinReceivableSchema, BillLinReceivableAddressSchema, BillLinReceivableSlipSchema, BillLinReceivableInactiveSchema, WORKER_SCHEMAS, RECEIVABLE_SCHEMAS, defineSchema } from './schemas/index'

// Hooks
export { useTransactions } from './hooks/useTransactions'
export { usePatchTransaction } from './hooks/usePatchTransaction'
export { useOnlineStatus } from './hooks/useOnlineStatus'
export { useWriteQueue } from './hooks/useWriteQueue'
export { useElementWidth } from './hooks/useElementWidth'
export { useWideDataLayout, DATA_LAYOUT_DESKTOP_MIN_PX } from './hooks/useWideDataLayout'

// Types
export type {
  WorkerSchema,
  ColumnSchema,
  DrawerSection,
  FilterConfig,
  WorkspaceConfig,
  Transaction,
  IncomeRecord,
  ExpenseRecord,
  CellType,
  CellRendererFn,
  ApiFetch,
  DataTableTheme,
  AeraDataTableProps,
  DrawerAction,
  TableFilters,
  TransactionPageMeta,
  TransactionPageResponse,
  TransactionSummary,
} from './types'

// Utilities
export {
  formatAmount,
  formatExpenseDocumentType,
  normalizeExpenseDocumentType,
  formatThaiDate,
  formatThaiDateFromDate,
  formatThaiDateCard,
  formatThaiDateCardFromDate,
  formatThaiDatetimePartsFromDate,
  formatThaiDatetime,
  formatThaiDatetimeFromDate,
  parseDatetimeInput,
  BANK_LOGOS,
  THAI_BANKS,
  normalizeThaiBankKey,
} from './utils/formatters'
export { buildColumnDefs } from './utils/buildColumnDefs'
export { cn } from './utils/cn'
export { DEFAULT_DATA_TABLE_THEME, buildDataTableThemeVars } from './utils/theme'
export { ICON_REGISTRY, normalizeIconKey, resolveIconComponent, ResolvedIcon, useResolvedIcon } from './utils/iconRegistry'
export type { IconKey, IconOverrideMap } from './utils/iconRegistry'
export { EMOJI_ICON_MAP, WORKER_ID_ICON_MAP, stripWorkerLabelEmoji, getLeadingEmoji, resolveWorkerIconKey, normalizeWorkerTab } from './utils/workerIcons'
export {
  STATUS_EMOJI_ICON_MAP,
  stripStatusEmoji,
  getLeadingStatusEmoji,
  resolveStatusVisual,
  isCancelledTransactionStatus,
} from './utils/statusVisuals'
export {
  fetchTransactionHistory,
  buildTransactionHistorySummary,
  pickLatestTransactionHistoryMeta,
  HistoryLoadError,
} from './utils/transactionHistory'
export type {
  TransactionHistoryEntry,
  TransactionHistorySummary,
} from './utils/transactionHistory'
export { getDisplayText, formatReferenceNo, isReferenceField, isTableReferenceField, maskTableReferenceNo } from './utils/display'
export {
  COLUMN_WIDTH_STATUS,
  resolveColumnWidth,
  columnWidthPercents,
} from './utils/columnSizing'
export { resolveSlipMatchedAmount } from './utils/incomeDerived'
export {
  SIMILAR_NAME_CHIP_LIMIT,
  SIMILAR_NAME_FILTER_KEY,
  CLIENT_ONLY_TABLE_FILTER_KEYS,
  buildSimilarNameChips,
  nameMatchesToken,
  getLedgerNameLabel,
} from './utils/similarNameChips'
export {
  formatHistoryActorLabel,
  filterHistoryDetailsForDisplay,
  filterHistoryEntriesForDisplay,
  formatReceivableStatusLabel,
  parseHistoryStatusDetailLine,
} from './utils/historyDisplay'
export {
  HistoryAmountText,
  historyAmountToneClass,
  renderHistoryAmountText,
  resolveHistoryEntryAmountTone,
  resolveReceivableHistoryAmountTone,
} from './utils/historyAmountHighlight'
export type { HistoryAmountTone } from './utils/historyAmountHighlight'
